import { Dispatch } from 'redux';
import { patchSceneAsset } from '@/features/sceneAssets/sceneAssetsActions';
import { upsertClip, selectClipsByAssetId } from '@/features/clips/clipsSlice';
import { ClipInstance, isFadeOutSource } from '@/types/clipTypes';
import type { AssetInstance } from '@/types/assetTypes';

import type { WriteScope } from '@/modules/undoRedo';
interface ExtendAssetParams {
  assetInstanceId: string;
  newEndTime: number;
  dispatch: Dispatch<any>;
  services: any;
  getState: () => any;
  /** The gesture the extension is part of. */
  scope?: WriteScope;
}

/**
 * Extends an asset's end time and repositions the fade-out clip.
 * Updates Redux immediately for instant visual feedback, then updates database in background;
 * resolves when the writes it started have landed.
 */
export const extendAssetEnd = ({
  assetInstanceId,
  newEndTime,
  dispatch,
  services,
  getState,
  scope
}: ExtendAssetParams): Promise<unknown> => {
  // The asset as the store holds it as this runs: one already reaching the new end (extended meanwhile) stays as it is
  const asset = (getState().sceneAssets.sceneAssetsArray as AssetInstance[]).find((a) => a.instanceId === assetInstanceId);
  if (!asset || newEndTime <= asset.instanceEndTime) return Promise.resolve([]);
  const writes: Promise<unknown>[] = [];
  // Update Redux state immediately for instant visual feedback
  dispatch(patchSceneAsset(assetInstanceId, { instanceEndTime: newEndTime }));

  // Move fade-out clip to new asset end position
  const assetClips = selectClipsByAssetId(getState(), assetInstanceId);
  const fadeOutClip = assetClips.find((c: ClipInstance) => isFadeOutSource(c.instanceSourceName));

  // Move fade-out clip regardless of duration (fade clips often have duration 0)
  if (fadeOutClip) {
    const updatedFadeOutClip = {
      ...fadeOutClip,
      instanceEndTime: newEndTime,
      instanceStartTime: newEndTime - (fadeOutClip.instanceDuration || 0)
    };

    // Update Redux immediately
    dispatch(upsertClip(updatedFadeOutClip));

    // Update database in background
    writes.push(services.clip.updateClipInstanceTime(
      fadeOutClip.instanceId,
      updatedFadeOutClip.instanceStartTime,
      updatedFadeOutClip.instanceEndTime,
      undefined,
      scope
    ));
  }

  // Only the end, so a move of the asset's start saved meanwhile stays
  writes.push(services.asset.updateAssetInstanceEndTime(assetInstanceId, newEndTime, scope));
  return Promise.allSettled(writes);
};
